/**
 * NotifichePage — sezione unificata Alert + Messaggi.
 *
 * Tab "Alert": legge `contract.alerts[]` dal contratto attivo (autolettura,
 *              bolletta, ecc.). Niente concetto di "letto/non letto".
 * Tab "Messaggi": legge ListMessaggi via TanStack Query. Ogni messaggio ha
 *              `letto: boolean`. Tocca → `markMessaggioAsRead` (optimistic).
 *
 * NB: all'apertura del tab Messaggi marca automaticamente tutti i non letti
 *     come letti (stesso pattern mobile `Notifications.js`).
 */

import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AlertCircle, Bell, ChevronLeft, Inbox } from 'lucide-react'
import { Link } from 'react-router-dom'
import { EmptyState } from '@/components/ui/empty-state'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useActiveContract } from '@/features/auth/hooks/useActiveContract'
import {
  useBollette,
  useMarkMessaggioRead,
  useMessaggi,
} from '@/lib/graphql/hooks'
import type { Messaggio } from '@/lib/graphql/types'
import { AlertList } from './components/AlertList'
import { MessaggiList } from './components/MessaggiList'

export function NotifichePage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { cliente, activeContract, isLoading: contractLoading, error: contractError } =
    useActiveContract()

  const initialTab =
    searchParams.get('tab') === 'messaggi' ? 'messaggi' : 'alert'

  const messaggiQ = useMessaggi(cliente?.codice_cliente)
  const bolletteQ = useBollette(
    cliente?.codice_cliente,
    activeContract?.codice_contratto,
  )
  const markReadMutation = useMarkMessaggioRead()

  const alerts = activeContract?.alerts ?? []
  const messages = messaggiQ.data ?? []
  const unreadCount = messages.filter((m) => !m.letto).length

  // Auto-mark all messages as read quando l'utente apre il tab Messaggi.
  // Pattern mobile `Notifications.js:77-95`.
  useEffect(() => {
    if (initialTab !== 'messaggi') return
    if (!cliente?.codice_cliente) return
    const unread = messages.filter((m) => !m.letto)
    if (unread.length === 0) return

    // Fire-and-forget per ogni messaggio non letto.
    unread.forEach((m) => {
      markReadMutation.mutate(
        { codice_cliente: cliente.codice_cliente, datetime: m.datetime },
        {
          onError: (err) => {
            console.warn('[NotifichePage] auto markAsRead failed:', err)
          },
        },
      )
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTab, messages.length, cliente?.codice_cliente])

  function handleMarkRead(message: Messaggio) {
    if (!cliente?.codice_cliente) return
    markReadMutation.mutate({
      codice_cliente: cliente.codice_cliente,
      datetime: message.datetime,
    })
  }

  function handleTabChange(value: string) {
    setSearchParams(
      value === 'messaggi' ? { tab: 'messaggi' } : {},
      { replace: true },
    )
  }

  if (contractError) {
    return (
      <Wrapper>
        <EmptyState
          icon={<AlertCircle className="h-5 w-5 text-destructive" />}
          title="Errore nel caricamento"
          description={contractError.message}
        />
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      <Tabs
        defaultValue={initialTab}
        onValueChange={handleTabChange}
        className="flex flex-col gap-4"
      >
        <TabsList className="self-start">
          <TabsTrigger value="alert">
            <Bell className="h-4 w-4" />
            Alert
            {alerts.length > 0 && (
              <span className="ml-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-warning/30 px-1.5 text-[10px] font-bold text-warning-foreground">
                {alerts.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="messaggi">
            <Inbox className="h-4 w-4" />
            Messaggi
            {unreadCount > 0 && (
              <span className="ml-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-secondary px-1.5 text-[10px] font-bold text-secondary-foreground">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="alert" className="mt-0">
          {contractLoading ? (
            <p className="text-sm text-muted-foreground">Caricamento alert…</p>
          ) : (
            <AlertList alerts={alerts} bollette={bolletteQ.data} />
          )}
        </TabsContent>

        <TabsContent value="messaggi" className="mt-0">
          {messaggiQ.error ? (
            <EmptyState
              icon={<AlertCircle className="h-5 w-5 text-destructive" />}
              title="Errore nel caricamento dei messaggi"
              description={messaggiQ.error.message}
            />
          ) : (
            <MessaggiList
              messages={messaggiQ.data}
              isLoading={messaggiQ.isLoading}
              onMarkRead={handleMarkRead}
            />
          )}
        </TabsContent>
      </Tabs>
    </Wrapper>
  )
}

function Wrapper({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4">
      <Link
        to="/"
        className="inline-flex w-fit items-center gap-1 text-sm font-semibold text-secondary hover:underline"
      >
        <ChevronLeft className="h-4 w-4" />
        Home
      </Link>
      <header>
        <h1 className="text-2xl font-bold text-foreground md:text-3xl">Notifiche</h1>
        <p className="text-sm text-muted-foreground">
          Alert sul contratto attivo e messaggi inviati dall’assistenza.
        </p>
      </header>
      {children}
    </div>
  )
}
